import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useState } from 'react';
import VotingSessionController from './controllers/VotingSessionController';
import type { RecordId } from '../../../types';
import classNames from 'classnames';
import styles from './VotingSession.module.css';
import SlideOutPanel from './SlideOutPanel';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import { hasVotingBehaviors } from '../../../objects/guards';
import { pluralize } from '@aha-app/aha-shared';

export const votingObjectIcon = (object: fabric.Object | undefined) =>
  object && hasVotingBehaviors(object) ? (
    <span className='color-tertiary'>{object.votingResultIcon}</span>
  ) : (
    <i
      className={classNames(['fa-regular', 'fa-question', 'color-tertiary'])}
    />
  );

const Result: React.FC<{ widget: VotingSessionWidget; objectId: string }> = ({
  widget,
  objectId,
}) => {
  const [detailsShown, setDetailsShown] = useState(false);

  const controller = useController(VotingSessionController);
  const object = controller.getObject(objectId);

  if (!widget) return null;

  const icon = votingObjectIcon(object);

  const content =
    object && hasVotingBehaviors(object) ? object.votingResultSummary : '';
  const voteCount = widget.votesForObject(objectId);
  const votes = [
    ...Object.entries(widget.votesByObjectAndUser()[objectId] ?? {}),
  ].sort(([, count1], [, count2]) => count2 - count1) as [RecordId, number][];

  return (
    <li>
      <div
        className={classNames('px-2 py-3', styles.votingResultEntry, {
          'side-panel__hover-background': !detailsShown,
          [styles.votingResultAction]: object,
        })}
        onClick={() => {
          if (!detailsShown) {
            controller.actionShowObject(objectId);
          }
          setDetailsShown(!detailsShown);
        }}
      >
        <div className={classNames('flex-col gap-2')}>
          <div className='flex-row flex-row--align-center gap-2'>
            <span className={classNames(styles.votingResultIcon, 'h-500')}>
              {icon}
            </span>
            <div
              className={classNames(
                'flex-row flex-row--justify-space-between flex-row--align-center gap-2',
                styles.flexGrow
              )}
            >
              <p className='mb-0'>
                <span className='h-500 font-500'>{voteCount}</span>&nbsp;
                <span className='t-200 color-tertiary'>
                  {pluralize(voteCount, 'vote')}
                </span>
              </p>
              <div className={styles.votingResultActions}>
                {!object && (
                  <aha-pill
                    color='var(--theme-secondary-background)'
                    text-color='var(--theme-secondary-text)'
                    border-shape='round'
                  >
                    Object deleted
                  </aha-pill>
                )}
                <i
                  className={`fa-regular fa-chevron-${
                    detailsShown ? 'up' : 'down'
                  }`}
                />
              </div>
            </div>
          </div>
          <p
            className={classNames(
              't-100',
              'mb-0',
              classNames(styles.votingResultSummaryText)
            )}
            title={content}
          >
            {content}
          </p>
        </div>
      </div>
      <SlideOutPanel direction='vertical' open={detailsShown}>
        <ol className='unstyled pt-3'>
          {votes.map(([userId, count]) => (
            <li
              key={userId}
              className='ml-6 mr-4 p-2 side-panel__hover-background'
            >
              <div className='flex-row flex-row--align-center gap-2'>
                <span className={styles.voteCountVoter}>
                  {controller.getResolvedUser(userId)}
                </span>
                <span className={classNames(styles.voteCountSeparator)} />
                <span>{count}</span>
              </div>
            </li>
          ))}
        </ol>
        <div className='text-right pt-2 pb-7 mr-4'>
          <aha-button
            kind='ghost'
            onClick={() => {
              setDetailsShown(false);
            }}
          >
            <span className='t-200 color-tertiary'>Hide details</span>
          </aha-button>
        </div>
      </SlideOutPanel>
    </li>
  );
};

export default ApplicationView(Result);
